<template>
    <popup-modal customClasses="w-768" @close="$emit('close')">
        <p class="text-heading-2 font-semibold first-letter:uppercase mb-20 mr-auto">
            {{ $t('interview.cancel_confirmation_title') }}
        </p>

        <checkbox v-model="sendCancellationMail"
                  class="mr-8 my-16 whitespace-nowrap"
        >
            <span class="font-normal">{{ $t('interview.send_cancel_mail') }}</span>
        </checkbox>

        <div v-if="sendCancellationMail" class="border border-primary-5 bg-primary-5 rounded-4 px-24 pb-24 my-32 w-672">
            <button
                class="trans-ease flex items-center text-black text-body w-full py-16"
                role="button"
                @click="showForm = !showForm"
            >
                {{ $t('interview.cancel_message') }}
                <svg-icon v-if="showForm" class="max-w-24 max-h-24 ml-12 text-secondary-80"
                          type="keyboard-arrow-up"></svg-icon>
                <svg-icon v-else class="max-w-24 max-h-24 ml-12 text-secondary-80"
                          type="keyboard-arrow-down"></svg-icon>
            </button>

            <div v-if="showForm">

                <select-dropdown
                    v-model="selectedTemplate"
                    :auto-translate="false"
                    class="mb-16"
                    :options="templateChoices"
                    no-auto-sort
                    white-background
                    @change="hydrateFromTemplate"
                >
                    {{ $t('mailer.choose_template') }}

                    <tabs
                        v-if="languages"
                        :active-key="templateLocale"
                        class="mb-12"
                        :items="languages"
                        scroll
                        @set-active="hydrateFromTemplate"
                    />
                    <span v-if="currentLocale !== this.templateLocale">{{ $t('interview.error_candidate_template_language') }}</span>
                </select-dropdown>

                <simple-input v-model="cancellationMail.subject"
                              class="my-16 capitalize"
                >
                    {{ $t('settings.email-templates.subject') }}
                </simple-input>

                <wysiwyg
                    ref="content"
                    :placeholders="['application', 'company', 'user']"
                    class="mt-16"
                    v-model="cancellationMail.message"
                >
                    {{ $t('form.content') }}

                    <template #after>
                        <div
                            :class="[ signaturePreview ? 'items-start' : 'items-center text-primary-40' ]"
                            class="flex p-16 border-t border-primary-20"
                        >
                            <div class="text-body flex-grow">
                                <span v-if="signaturePreview" v-html="signaturePreview"></span>
                                <span v-else>{{ $t('email_signatures.no_signature_selected') }}</span>
                            </div>
                            <select-dropdown
                                v-model="selectedSignature"
                                :auto-translate="false"
                                :options="signatureChoices"
                                class="min-w-240"
                            />
                        </div>
                    </template>
                </wysiwyg>
            </div>
        </div>

        <div class="mt-32 flex justify-end">
            <simple-button
                :loading="loading"
                display="danger"
                look="primary"
                type="button"
                @click="$emit('confirm', signature)"
            >
                {{ $t('interview.cancel.simple') }}
            </simple-button>
        </div>
    </popup-modal>
</template>

<script>
import Wysiwyg from "./forms/Wysiwyg";
import PopupModal from "./ui/PopupModal";
import SimpleButton from "./ui/SimpleButton";
import SvgIcon from './ui/SvgIcon.vue';
import Input from "./forms/Input";
import * as signatures from "../services/EmailSignatureService";
import EmailSignature from "../models/EmailSignature";
import {normalizeJson} from "../../shared/helpers";
import * as templates from "../services/EmailTemplateService";
import EmailTemplate from "../models/EmailTemplate";
import SelectDropdown from "./forms/SelectDropdown.vue";
import {mapState, mapActions} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import Tabs from './navigation/Tabs';
import Checkbox from "./forms/Checkbox.vue";

export default {
    components: {
        Checkbox,
        Tabs,
        SelectDropdown,
        PopupModal,
        SimpleButton,
        Wysiwyg,
        SvgIcon,
        'simple-input': Input,
    },
    props: {
        loading: Boolean,
        value: Object,
        sendMail: Boolean,
    },
    data: function () {
        return {
            showForm: true,
            fetchingTemplates: true,
            templateLocale: 'en',
            selectedTemplate: 0,
            selectedSignature: 0,
            sendCancellationMail: this.sendMail,
        }
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany', 'selectedLocale', 'locale']),
        ...mapState(useAuthenticationStore, ['defaultSignature']),
        active_locale() {
            return this.currentCompany.jobsite.active_languages.includes(this.locale) ? this.locale : this.currentCompany.jobsite.active_languages[0];
        },
        currentLocale() {
            return this.selectedLocale || this.currentCompany.locale;
        },
        cancellationMail: {
            get() {
                return this.value
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        languages() {
            if (this.currentCompany.jobsite.active_languages.length === 1) {
                return;
            }

            let languages = {};

            this.currentCompany.jobsite.active_languages.forEach((field, index) => {
                languages[field] = {
                    title: field,
                };
            });

            return languages;
        },
        templates() {
            return useRepo(EmailTemplate)
                .with('attachments')
                .where('type', 'interview')
                .orderBy('order')
                .get();
        },
        templateChoices() {
            return [
                {value: 0, label: '--- ' + this.$t('general.select') + ' ---'},
                ...this.templates.map(template => ({
                    value: template.id || template.core_template,
                    label: template.name[this.templateLocale],
                })),
            ];
        },
        signatures() {
            return useRepo(EmailSignature).orderBy('created_at').get();
        },
        signatureChoices() {
            return [
                {value: 0, label: this.$t('email_signatures.no_signature_selected')},
                ...this.signatures.map(signature => ({
                    value: signature.id,
                    label: signature.name,
                })),
            ];
        },
        signaturePreview() {
            return this.signatures.find(signature => signature.id === this.selectedSignature)?.content;
        },
        signature() {
            return (this.signaturePreview ? ('<br>' + this.signaturePreview) : '')
        }
    },
    methods: {
        cancelInterview() {
            this.$emit('confirm');
        },
        hydrateFromTemplate(data) {
            let lang = isNaN(data) ? data : this.active_locale;
            let templateId = !isNaN(data) ? data : this.selectedTemplate;

            this.templateLocale = lang;

            let template = this.templates.find(template => template.id === templateId);
            //Always empty attachments
            this.cancellationMail.attachments = [];

            if (typeof template != 'undefined') {
                //Check for attachments
                if (template.attachments.length > 0) {
                    //Add attachments to form by locale
                    this.cancellationMail.attachments = template.attachments.filter(obj => obj.locale === this.active_locale);
                }

                this.cancellationMail.subject = template.subject[this.templateLocale];
                this.$refs.content.setContent(template.content[this.templateLocale]);
            }
        },
    },
    watch: {
        'sendCancellationMail': function (val) {
            this.$emit('changeSendMail', val);
        },
    },
    mounted() {
        if (this.currentLocale) {
            this.templateLocale = this.currentLocale;
        }

        this.fetchingTemplates = true;

        signatures.index().then(result => {
            return useRepo(EmailSignature).save(normalizeJson(result.data));
        }).then(() => {
            this.selectedSignature = this.defaultSignature?.id || 0;
        });

        templates.index().then(result => {
            return useRepo(EmailTemplate).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingTemplates = false;
        });
    }
}
</script>
